import { useEditorStore } from "@core-ui/stores/EditorStore";
import { setOnlyChangesEnabled, useOnlyChangesEnabled } from "@ext/git/core/Diff/components/store/DiffOnlyChangesStore";
import { type ChangeStop, nextStopIndex } from "@ext/git/core/Diff/logic/changeStops";
import { expandDeletedAt } from "@ext/git/core/Diff/logic/collapseDeleted";
import { onlyChangesKey, setOnlyChanges, visiblePos } from "@ext/git/core/Diff/logic/onlyChanges";
import scrollToDiff from "@ext/git/core/Diff/logic/scrollToDiff";
import t from "@ext/localization/locale/translate";
import type { Editor } from "@tiptap/react";
import {
	GlassToolbar,
	GlassToolbarButton,
	GlassToolbarIcon,
	GlassToolbarSeparator,
	GlassToolbarText,
	GlassToolbarToggleGroup,
	GlassToolbarToggleItem,
} from "@ui-kit/GlassToolbar";
import { useEffect, useRef, useState } from "react";

const DASH = "–";

interface DiffNavigationPanelProps {
	editor: Editor;
}

const NO_STOPS: ChangeStop[] = [];

const TARGET_CLASS = "diff-line-target";
const TARGET_MS = 1200;

const highlightLine = (pos: number) => {
	const element = document.querySelector<HTMLElement>(`[data-type="diff-line"][data-from="${pos}"]`);
	if (!element) return;
	element.classList.add(TARGET_CLASS);
	setTimeout(() => element.classList.remove(TARGET_CLASS), TARGET_MS);
};

const DiffNavigationPanel = ({ editor }: DiffNavigationPanelProps) => {
	const enabled = useOnlyChangesEnabled();

	const [stops, setStops] = useState<ChangeStop[]>(NO_STOPS);
	const [current, setCurrent] = useState<number | null>(null);
	const anchorRef = useRef<number | null>(null);
	const signatureRef = useRef("");

	useEffect(() => {
		if (!editor || editor.isDestroyed) return;

		const sync = () => {
			const next = onlyChangesKey.getState(editor.state)?.stops ?? NO_STOPS;
			setStops((previous) => {
				if (previous === next) return previous;

				// Compare positions, not length: an edit can drop one change and add another at the same count.
				const signature = next.map((stop) => stop.pos).join(",");
				if (signature !== signatureRef.current) {
					signatureRef.current = signature;
					setCurrent(null);
				}

				return next;
			});
		};

		sync();
		editor.on("transaction", sync);
		return () => {
			editor.off("transaction", sync);
		};
	}, [editor]);

	// The mode store is app-wide; without this reset the next article opens collapsed.
	useEffect(() => {
		return () => setOnlyChangesEnabled(false);
	}, []);

	useEffect(() => {
		if (!editor || editor.isDestroyed) return;

		setOnlyChanges(editor.view, enabled);

		const anchor = anchorRef.current;
		anchorRef.current = null;
		if (anchor !== null) scrollToDiff(editor.view, visiblePos(editor.view.state, anchor));
	}, [enabled, editor]);

	if (!editor || (!stops.length && !enabled)) return null;

	const go = (direction: 1 | -1) => {
		const index = nextStopIndex(stops.length, current, direction);
		if (index === null) return;

		setCurrent(index);

		const stop = stops[index];

		const view = editor.view;

		// Scroll to the marker, not the pos: expanded text renders before the pos, so the pos lands at its end.
		if (stop.isDeleted) {
			const key = expandDeletedAt(view, stop.pos);
			const marker = key && view.dom.querySelector<HTMLElement>(`.diff-deleted-marker[data-diff-key="${key}"]`);
			if (marker) {
				scrollToDiff(view, marker);
				return;
			}
		}

		scrollToDiff(view, visiblePos(view.state, stop.pos));
		highlightLine(stop.pos);
	};

	const toggle = () => {
		const view = editor.view;
		const box = view.dom.getBoundingClientRect();
		const top = Math.max(box.top, 0) + 1;
		anchorRef.current = view.posAtCoords({ left: box.left + 1, top })?.pos ?? null;
		setOnlyChangesEnabled(!enabled);
	};

	const hasStops = stops.length > 0;
	const counter = hasStops ? (current === null ? DASH : String(current + 1)) : "0";
	const toggleLabel = enabled ? t("diff.only-changes.expand") : t("diff.only-changes.collapse");

	return (
		<GlassToolbar
			aria-label={t("diff.only-changes.panel")}
			className="mx-auto mb-1 w-fit"
			data-qa="diff-navigation-panel"
		>
			<GlassToolbarButton
				className="disabled:pointer-events-none disabled:opacity-50"
				disabled={!hasStops}
				onClick={() => go(-1)}
				tooltipText={t("diff.only-changes.previous")}
			>
				<GlassToolbarIcon icon="arrow-up" />
			</GlassToolbarButton>
			<GlassToolbarText
				className="whitespace-nowrap px-1 font-mono text-xs"
				role="status"
				title={t("diff.only-changes.title")}
			>
				{`${counter} / ${stops.length}`}
			</GlassToolbarText>
			<GlassToolbarButton
				className="disabled:pointer-events-none disabled:opacity-50"
				disabled={!hasStops}
				onClick={() => go(1)}
				tooltipText={t("diff.only-changes.next")}
			>
				<GlassToolbarIcon icon="arrow-down" />
			</GlassToolbarButton>
			<GlassToolbarSeparator />
			{/* onClick, not onValueChange: the ui-kit wrapper preventDefaults the click and Radix then skips its handler. */}
			<GlassToolbarToggleGroup type="single" value={enabled ? "on" : ""}>
				<GlassToolbarToggleItem aria-label={toggleLabel} onClick={toggle} tooltipText={toggleLabel} value="on">
					<GlassToolbarIcon icon={enabled ? "unfold-vertical" : "fold-vertical"} />
				</GlassToolbarToggleItem>
			</GlassToolbarToggleGroup>
		</GlassToolbar>
	);
};

// Must live in the sticky toolbar container: `position: fixed` under `contain: layout paint` anchors to the article, not the window.
export const ArticleDiffNavigation = () => {
	const editor = useEditorStore((state) => state.editor);
	if (!editor || editor.isDestroyed) return null;
	return <DiffNavigationPanel editor={editor} key={editor.instanceId} />;
};

export default DiffNavigationPanel;
